// This coordinates ReactTable and a ToolkitPropertyTableUI to display the
// recommended comps. We don't really want to keep using these things (most of
// the time react table isn't good for us because we need to use RHF, and we're
// deprecating ToolkitPropertyTable which uses old inputs - neither of which
// actually matter for this use case), but plan to totally change this UI in the
// near future, so this is OK for now to quickly get table rendering with the
// functionality that we do actually need.
import { AloftDate, ToolkitListingID, parseToolkitListingID } from '@aloft/toolkit-shared';

import { forwardRef, useImperativeHandle } from 'react';

import { useTkv2UpdateReferenceBarProperties } from '../../../controller/hooks/useTkv2UpdateReferenceBarProperties.ts';
import { formatReferenceViewID } from '../../../modules/referenceView/index.ts';
import { selectPropertyIdentificationById } from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectEffectiveDate } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import {
  LocationType,
  getLocationTypeProximityRequirementMiles,
} from '../../../utils/locationTypeProximityRequirementMiles.ts';
import { getLocationTypeSaleDateRequirementMonths } from '../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { ToolkitPropertyTableUI } from '../../ToolkitPropertyTable/ToolkitPropertyTableUI.tsx';
import { useRecommendedCompSelectionTable } from '../hooks/useRecommendedCompSelectionTable.tsx';
import { CompSelectionProperty, RecommendedCompSelectionGridProps } from '../types.ts';

export const CompSelectionGrid = forwardRef(
  (
    {
      data,
      subject,
      comps,
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      focusedPropertyIndex,
      setFocusedRow,
      source,
      orderID,
      isInspectifyOrder,
      hasArvOption,
      compSelectionTableVariant,
    }: RecommendedCompSelectionGridProps,
    ref,
  ) => {
    const subjectPropertyIdentification = useAppSelector((state) =>
      selectPropertyIdentificationById(state, 'subject'),
    );
    const locationType = subjectPropertyIdentification?.locationType as LocationType | undefined;
    const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(locationType);
    const saleDateRequirementMonths = getLocationTypeSaleDateRequirementMonths(locationType);
    const effectiveDate = useAppSelector(selectEffectiveDate);
    const effectiveDateAloft = effectiveDate ? AloftDate.schema.parse(effectiveDate) : undefined;
    const { table, rowsToPin, tableData } = useRecommendedCompSelectionTable(
      data,
      subject,
      comps,
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      source,
      orderID,
      isInspectifyOrder,
      hasArvOption,
      locationType,
      proximityRequirementMiles,
      effectiveDateAloft,
      saleDateRequirementMonths,
      compSelectionTableVariant,
    );
    const updateRefBarProperties = useTkv2UpdateReferenceBarProperties();

    useImperativeHandle(ref, () => table, [table]);

    const handleRowClick = (row: CompSelectionProperty) => {
      if (row.id === 'subject') {
        updateRefBarProperties({
          id: formatReferenceViewID({ type: 'subject' }),
          mapPinLabel: '',
        });
      } else {
        const parsedListingID = parseToolkitListingID(row.listingID as ToolkitListingID);
        if (parsedListingID) {
          updateRefBarProperties({
            id: formatReferenceViewID(parsedListingID),
            mapPinLabel:
              tableData.indexOf(row) !== undefined
                ? (tableData.indexOf(row) - 1 + rowsToPin).toString()
                : '',
          });
        }
      }
      setFocusedRow(row.id);
    };

    const getRowBackgroundColor = (row: { original: CompSelectionProperty }) => {
      // Only apply colors when AIV/ARV option is available
      if (!isInspectifyOrder || !hasArvOption) {
        return undefined;
      }

      // Don't color the subject row
      if (row.original.id === 'subject') {
        return undefined;
      }

      const selection = rowSelection[row.original.id];
      if (selection?.aiv) {
        return '#FFF6C7'; // AIV yellow
      }
      if (selection?.arv) {
        return '#E0F4F0'; // ARV green
      }
      return undefined;
    };

    return (
      <ToolkitPropertyTableUI
        table={table}
        shouldPinFirstRow={rowsToPin}
        onRowClick={handleRowClick}
        activeRowIndex={focusedPropertyIndex}
        getRowBackgroundColor={getRowBackgroundColor}
        useVirtualization={false}
      />
    );
  },
);
